import React, { useState, useContext, useEffect } from 'react';
import { TIMEZONE_LIST } from '../../utils/timezones';
import { useAuctionHouses, useCreateAuctionHouse, useUpdateAuctionHouse, useDeleteAuctionHouse, useUploadAuctionHouseLogo } from './hooks/useAuctionHouses';
import { images } from '../../utils/api';
import { usePreferences } from '../../hooks/usePreferences';
import BaseTable from '../../components/shared/table/BaseTable';
import { TableProvider, TableContext } from '../../components/shared/table/context/TableContext';
import { AuctionHousesToolbar } from './AuctionHousesToolbar';

// Content component that uses TableContext
function AuctionHousesContent({ columns, data, onAdd, loading, error }) {
  const { setColumns } = useContext(TableContext);

  useEffect(() => {
    setColumns(columns);
  }, [columns, setColumns]);

  return (
    <>
      <AuctionHousesToolbar
        columns={columns}
        data={data}
        onAdd={onAdd}
      />
      <BaseTable
        data={data}
        columns={columns}
        loading={loading}
        error={error}
      />
    </>
  );
}

// Form for creating/editing auction houses
function AuctionHouseForm({ auctionHouse, onSubmit, onCancel }) {
  const [formData, setFormData] = useState(auctionHouse || {
    name: '',
    website: '',
    default_currency: 'USD',
    default_buyers_premium_percent: 20.00,
    default_buyers_premium_minimum: 0.00,
    default_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  const [logoFile, setLogoFile] = useState(null);
  const [error, setError] = useState(null);
  const uploadLogoMutation = useUploadAuctionHouseLogo();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // First submit the form data
      const result = await onSubmit(formData);
      
      // If there's a new logo file and we have an auction house ID (either from update or create)
      if (logoFile && (auctionHouse?.id || result?.id)) {
        const auctionHouseId = auctionHouse?.id || result?.id;
        const formData = new FormData();
        formData.append('logo', logoFile);
        await uploadLogoMutation.mutateAsync({
          id: auctionHouseId,
          formData: formData
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Logo file size must be less than 5MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        setError('Logo must be a JPEG, PNG, or GIF file');
        return;
      }
      setLogoFile(file);
      setError(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Logo
        </label>
        <div className="mt-1 flex items-center space-x-4">
          {auctionHouse?.logo_path && (
            <img
              src={images.getImage(auctionHouse.logo_path)}
              alt="Current logo"
              className="h-12 w-12 object-contain"
            />
          )}
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={handleLogoChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Upload a logo image (JPEG, PNG, or GIF, max 5MB)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Website
        </label>
        <input
          type="url"
          value={formData.website}
          onChange={e => setFormData(prev => ({ ...prev, website: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default Currency *
        </label>
        <select
          value={formData.default_currency}
          onChange={e => setFormData(prev => ({ ...prev, default_currency: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
          <option value="CAD">CAD</option>
          <option value="AUD">AUD</option>
          <option value="CHF">CHF</option>
          <option value="JPY">JPY</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default Buyer's Premium % *
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={formData.default_buyers_premium_percent}
          onChange={e => setFormData(prev => ({ 
            ...prev, 
            default_buyers_premium_percent: parseFloat(e.target.value) || 0 
          }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default Buyer's Premium Minimum *
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formData.default_buyers_premium_minimum}
          onChange={e => setFormData(prev => ({ 
            ...prev, 
            default_buyers_premium_minimum: parseFloat(e.target.value) || 0 
          }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default Timezone *
        </label>
        <select
          value={formData.default_timezone}
          onChange={e => setFormData(prev => ({ ...prev, default_timezone: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        >
          {TIMEZONE_LIST.map(tz => (
            <option key={tz} value={tz}>
              {tz} ({new Date().toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit' })})
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {auctionHouse ? 'Update' : 'Create'} Auction House
        </button>
      </div>
    </form>
  );
}

// Main auction houses manager component
export function AuctionHousesManager() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAuctionHouse, setSelectedAuctionHouse] = useState(null);
  const { data: auctionHouses, isLoading: dataLoading, error: dataError } = useAuctionHouses();
  const { preferences, loading: prefsLoading, error: prefsError } = usePreferences('auction_houses');
  
  const createMutation = useCreateAuctionHouse();
  const updateMutation = useUpdateAuctionHouse();
  const deleteMutation = useDeleteAuctionHouse();

  const isLoading = dataLoading || prefsLoading;
  const error = dataError || prefsError;

  const columns = React.useMemo(() => [
    {
      headerName: 'Name',
      field: 'name',
      renderCell: (item) => {
        return (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedAuctionHouse(item);
              setTimeout(() => setIsModalOpen(true), 0);
            }}
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {item.name}
          </button>
        );
      }
    },
    {
      headerName: 'Website',
      field: 'website',
      renderCell: (item) => item.website ? (
        <a 
          href={item.website} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          {item.website}
        </a>
      ) : null
    },
    {
      headerName: 'Currency',
      field: 'default_currency'
    },
    {
      headerName: 'Buyer\'s Premium',
      field: 'default_buyers_premium_percent',
      renderCell: (item) => `${item.default_buyers_premium_percent}%`
    },
    {
      headerName: 'Premium Minimum',
      field: 'default_buyers_premium_minimum',
      renderCell: (item) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: item.default_currency
        }).format(item.default_buyers_premium_minimum);
      }
    },
    {
      headerName: 'Default Timezone',
      field: 'default_timezone',
      renderCell: (item) => `${item.default_timezone} (${new Date().toLocaleTimeString('en-US', { timeZone: item.default_timezone, hour: '2-digit', minute: '2-digit' })})`
    },
    {
      headerName: 'Actions',
      field: 'actions',
      renderCell: (item) => (
        <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
          <button
            className="px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => {
              setSelectedAuctionHouse(item);
              setIsModalOpen(true);
            }}
          >
            Edit
          </button>
          <button
            className="px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this auction house?')) {
                deleteMutation.mutate(item.id);
              }
            }}
          >
            Delete
          </button>
        </div>
      )
    }
  ], [deleteMutation]);

  return (
    <div>
      <div className="bg-white rounded-lg shadow flex-1 min-h-0 overflow-hidden">
        <TableProvider
          type="auction_houses"
          initialState={{
            loading: isLoading,
            error: error,
            preferences: preferences
          }}
        >
          <AuctionHousesContent 
            columns={columns}
            data={auctionHouses || []}
            onAdd={() => {
              setSelectedAuctionHouse(null);
              setIsModalOpen(true);
            }}
            loading={isLoading}
            error={error}
          />
        </TableProvider>
      </div>

      {/* Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => {
              setIsModalOpen(false);
              setSelectedAuctionHouse(null);
            }}></div>
            <div className="relative bg-white rounded-lg max-w-lg w-full">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium">
                  {selectedAuctionHouse ? 'Edit Auction House' : 'Add Auction House'}
                </h3>
              </div>
              <div className="p-6">
                <AuctionHouseForm
                  auctionHouse={selectedAuctionHouse}
                  onSubmit={(formData) => {
                    if (selectedAuctionHouse) {
                      updateMutation.mutate({ id: selectedAuctionHouse.id, ...formData });
                    } else {
                      createMutation.mutate(formData);
                    }
                    setIsModalOpen(false);
                    setSelectedAuctionHouse(null);
                  }}
                  onCancel={() => {
                    setIsModalOpen(false);
                    setSelectedAuctionHouse(null);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
